import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import VendorTraceFilters from './vendor_trace_filters/VendorTraceFilters';
import VendorTraceOverview from './vendor_trace_overview/VendorTraceOverview';
import {
    getMaxTraceLevelData,
    getVendorTraceData,
    getConsentTypesData,
} from '../../api/diagnose_dashboard/vendor_trace';
import {
    resetVendorTrace,
    setConsentTypeFilter,
    setMaxTraceLevel,
    setSelectedVendorTraceFilters,
} from '../../actions/diagnose_vendor_trace_actions';
import { Spin } from 'antd';
import useSearchParams from '../diagnose_dashboard/shared/useSearchParams';

const VendorTraceDashboard = ({ setSelectedVendorTraceFilters, setConsentTypeFilter, consentTypeFilter, selectedVendorTraceFilters, maxTraceLevel, setMaxTraceLevel, resetVendorTrace }) => {
    const [currentTraceLevel, setCurrentTraceLevel] = useState(0);
    const [maxTraceLevelLoading, setMaxTraceLevelLoading] = useState(false);
    const [consentTypes, setConsentTypes] = useState([]);
    const [consentTypesLoading, setConsentTypesLoading] = useState(false);
    const [vendorTraceData, setVendorTraceData] = useState([]);
    const [vendorTraceDataLoading, setVendorTraceDataLoading] = useState(false);
    const [filtersData, setFiltersData] = useState([]);
    const searchParams = useSearchParams();

    const VendorTraceOverviewRef = useRef(null);
    const executeScroll = () => VendorTraceOverviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });

    useEffect(() => {
        getVendorTrace(maxTraceLevel);
    }, [maxTraceLevel]);

    useEffect(() => {
        setVendorTraceData([]);
        setMaxTraceLevel(null);
        if (!_.isEmpty(selectedVendorTraceFilters) && consentTypeFilter) {
            getMaxTraceLevel(selectedVendorTraceFilters);
        }
    }, [consentTypeFilter]);

    const getConsentTypes = async (filters) => {
        setConsentTypesLoading(true);
        await getConsentTypesData({
            dateFrom: filters.scannedPeriod.dateFrom,
            dateTo: filters.scannedPeriod.dateTo,
            scheduledScanId: filters.scannedUrl,
        }).then((resp) => {
            setConsentTypes(resp);
            const selectedConsentType = resp?.find((consentType) => consentType?.id === searchParams.consent_type)?.id || resp[0]?.id;
            setConsentTypeFilter(selectedConsentType);
            setConsentTypesLoading(false);
        }).catch((e) => console.log(e));
    };

    const getMaxTraceLevel = async (filters) => {
        setMaxTraceLevelLoading(true);
        await getMaxTraceLevelData({
            dateFrom: filters.scannedPeriod.dateFrom,
            dateTo: filters.scannedPeriod.dateTo,
            scheduledScanId: filters.scannedUrl,
            regions: filters.region,
            consentFilter: consentTypeFilter,
            scanId: filters.scanIds,
        }).then((resp) => {
            setMaxTraceLevel(resp.max_level);
            setMaxTraceLevelLoading(false);
        }).catch((e) => console.log(e));
    };

    const getVendorTrace = async (maxTraceLevel) => {
        setVendorTraceDataLoading(true);
        const promises = Array.from({ length: maxTraceLevel }, (_, i) => getVendorTraceData({
            dateFrom: filtersData.scannedPeriod.dateFrom,
            dateTo: filtersData.scannedPeriod.dateTo,
            scheduledScanId: filtersData.scannedUrl,
            traceLevel: i + 1,
            regions: filtersData.region,
            consentFilter: searchParams.consent_type || consentTypeFilter,
            scanId: filtersData.scanIds,
        }));
        Promise.all(promises)
            .then((responses) => {
                responses.forEach((response, index) => {
                    setCurrentTraceLevel(index + 1);
                    setVendorTraceData((prevState) => [...prevState, response]);
                });
            })
            .catch((e) => console.log(e))
            .finally(() => 
                setVendorTraceDataLoading(false),
            );
    };

    useEffect(() => {
        return () => {
            resetVendorTrace();
        };
    }, []);

    return (
        <div className={'diagnose_container'}>
            <div className="diagnose_dashboard vendor_trace_dashboard">
                <div className="headers main_header">Vendor Trace</div>
                <div className="diagnose_dashboard-main-filters-hint">
                    The vendor trace allows sites to assess the vendors they have direct relationships and contracts
                    with and to share information about a vulnerability with them that is coming from the vendors
                    in their supply chain.
                </div>
                <div className="diagnose_dashboard-main-filters-block">
                    <VendorTraceFilters
                        onApply={async (filters) => {
                            setVendorTraceData([]);
                            setConsentTypeFilter('');
                            setFiltersData(filters);
                            setSelectedVendorTraceFilters(filters);
                            await getConsentTypes(filters);
                            executeScroll();
                        }}
                    />
                </div>
                <div ref={VendorTraceOverviewRef} id={'vendorTraceData'}>
                    <Spin spinning={maxTraceLevelLoading || vendorTraceDataLoading || consentTypesLoading} size="large" wrapperClassName={'vendor-trace-data-loading'}>
                        <VendorTraceOverview
                            vendorTraces={vendorTraceData}
                            maxTraceLevel={maxTraceLevel}
                            currentTraceLevel={currentTraceLevel}
                            consentTypes={consentTypes}
                            defaultConsentType={consentTypeFilter}
                        />
                    </Spin>
                </div>
            </div>
        </div>
    );
};
const mapStateToProps = function (store) {
    return {
        consentTypeFilter: store.diagnoseVendorTraceState.getIn(['consentTypeFilter', 'value']),
        selectedVendorTraceFilters: store.diagnoseVendorTraceState.getIn(['vendorTraceFilters', 'value']),
        maxTraceLevel: store.diagnoseVendorTraceState.getIn(['maxTraceLevel', 'value']),
    };
};

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators({
        setSelectedVendorTraceFilters,
        setConsentTypeFilter,
        setMaxTraceLevel,
        resetVendorTrace,
    }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(VendorTraceDashboard);
